import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  CalendarClock, Plus, Trash2, FileText, Mail, Clock, Users, Power, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQ_LABELS = {
  daily: 'Diário',
  weekly: 'Semanal',
  monthly: 'Mensal'
};

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

export function ScheduledReportsPage() {
  const [reports, setReports] = useState([]);
  const [hostGroups, setHostGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', org_id: '', group_ids: [], period_days: 7, formats: ['pdf', 'csv'],
    frequency: 'weekly', recipients: '', day_of_week: 1, day_of_month: 1, hour: 8, is_active: true
  });
  const [orgs, setOrgs] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const { getAuthHeader, user } = useAuthStore();

  const addGroup = (gid) => {
    if (gid && !form.group_ids.includes(gid)) {
      setForm(f => ({ ...f, group_ids: [...f.group_ids, gid] }));
    }
    setSelectedGroup('');
  };

  const fetchData = useCallback(async () => {
    try {
      const [reportsRes, groupsRes, orgsRes] = await Promise.all([
        axios.get(`${API}/scheduled-reports`, { headers: getAuthHeader() }),
        axios.get(`${API}/hostgroups`, { headers: getAuthHeader() }),
        axios.get(`${API}/orgs`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setReports(reportsRes.data);
      setHostGroups(groupsRes.data.groups || []);
      setOrgs(orgsRes.data);
    } catch (_) {
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.name || !form.recipients) {
      toast.error('Preencha nome e destinatários');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        org_id: form.org_id || null,
        recipients: form.recipients.split(',').map(e => e.trim()).filter(Boolean),
        group_ids: form.group_ids
      };
      await axios.post(`${API}/scheduled-reports`, payload, { headers: getAuthHeader() });
      toast.success('Relatório agendado criado');
      setDialogOpen(false);
      setForm({ name: '', org_id: '', group_ids: [], period_days: 7, formats: ['pdf', 'csv'], frequency: 'weekly', recipients: '', day_of_week: 1, day_of_month: 1, hour: 8, is_active: true });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar relatório');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (report) => {
    try {
      await axios.put(`${API}/scheduled-reports/${report.id}`, 
        { is_active: !report.is_active }, 
        { headers: getAuthHeader() }
      );
      setReports(prev => prev.map(r => r.id === report.id ? { ...r, is_active: !r.is_active } : r));
      toast.success(report.is_active ? 'Relatório desativado' : 'Relatório ativado');
    } catch (_) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este relatório agendado?')) return;
    try {
      await axios.delete(`${API}/scheduled-reports/${id}`, { headers: getAuthHeader() });
      toast.success('Relatório excluído');
      fetchData();
    } catch (_) {
      toast.error('Erro ao excluir');
    }
  };

  const handleRunNow = async (id) => {
    try {
      toast.info('Gerando e enviando relatório...');
      const res = await axios.post(`${API}/scheduled-reports/${id}/run`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Relatório enviado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar relatório');
    }
  };

  const formatSchedule = (r) => {
    const hour = `${String(r.hour).padStart(2, '0')}:00`;
    if (r.frequency === 'daily') return `Diário às ${hour}`;
    if (r.frequency === 'weekly') return `${DAY_NAMES[r.day_of_week || 0]} às ${hour}`;
    if (r.frequency === 'monthly') return `Dia ${r.day_of_month || 1} às ${hour}`;
    return '';
  };

  const isAdmin = user?.role === 'admin';

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CalendarClock className="h-5 w-5" /> Relatórios Agendados
          </h2>
          <p className="text-sm text-muted-foreground">
            Configure envio automático de relatórios PDF e CSV por email
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Novo Relatório
          </Button>
        )}
      </div>

      {reports.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <CalendarClock className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg">Nenhum relatório agendado</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Crie relatórios automáticos que serão enviados por email na frequência desejada.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {reports.map(r => (
            <Card key={r.id} className={!r.is_active ? 'opacity-60' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    <CardTitle className="text-base">{r.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    <Switch
                      checked={r.is_active}
                      onCheckedChange={() => handleToggle(r)}
                      className="scale-75"
                    />
                    {isAdmin && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Enviar agora" onClick={() => handleRunNow(r.id)}>
                        <Send className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(r.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatSchedule(r)}
                  </Badge>
                  <Badge variant="outline">{r.period_days || 7} dias</Badge>
                  {(r.formats || ['pdf', 'csv']).map(f => (
                    <Badge key={f} variant="outline" className="uppercase text-[10px]">{f}</Badge>
                  ))}
                  <Badge variant={r.is_active ? 'default' : 'outline'}>
                    <Power className="h-3 w-3 mr-1" />
                    {r.is_active ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" />
                  <span className="truncate">{(r.recipients || []).join(', ')}</span>
                </div>
                {r.group_ids?.length > 0 && (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Users className="h-3.5 w-3.5" />
                    <span>{r.group_ids.length} grupo(s)</span>
                  </div>
                )}
                {r.last_sent && (
                  <p className="text-xs text-muted-foreground">
                    Último envio: {new Date(r.last_sent).toLocaleDateString('pt-BR')}
                    {r.last_status === 'error' && <span className="text-red-500 ml-1">(falha)</span>}
                    {r.last_status === 'success' && <span className="text-green-500 ml-1">(sucesso)</span>}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg flex flex-col max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Novo Relatório Agendado</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div className="space-y-2">
              <Label>Nome do Relatório *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Relatório Semanal Servidores"
              />
            </div>

            <div className="space-y-2">
              <Label>Cliente/Org</Label>
              <Select value={form.org_id} onValueChange={v => setForm(f => ({ ...f, org_id: v, group_ids: [] }))}>
                <SelectTrigger data-testid="scheduled-org-select">
                  <SelectValue placeholder="Todos os grupos do tenant" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.display_name || o.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Define o escopo: todos os grupos do cliente por padrão</p>
            </div>

            <div className="space-y-2">
              <Label>Período do relatório</Label>
              <Select value={String(form.period_days)}
                onValueChange={v => setForm(f => ({ ...f, period_days: Number(v) }))}>
                <SelectTrigger data-testid="scheduled-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[7, 15, 30].map(d => <SelectItem key={d} value={String(d)}>Últimos {d} dias</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Formatos anexados</Label>
              <div className="flex gap-4">
                {['pdf', 'csv'].map(fmt => (
                  <div key={fmt} className="flex items-center gap-2">
                    <Checkbox id={`sfmt-${fmt}`} checked={form.formats.includes(fmt)}
                      onCheckedChange={() => setForm(f => ({
                        ...f,
                        formats: f.formats.includes(fmt) ? f.formats.filter(x => x !== fmt) : [...f.formats, fmt],
                      }))}
                      data-testid={`scheduled-format-${fmt}`} />
                    <label htmlFor={`sfmt-${fmt}`} className="text-sm uppercase cursor-pointer">{fmt}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Frequência</Label>
              <Select value={form.frequency} onValueChange={v => setForm(f => ({ ...f, frequency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Diário</SelectItem>
                  <SelectItem value="weekly">Semanal</SelectItem>
                  <SelectItem value="monthly">Mensal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.frequency === 'weekly' && (
              <div className="space-y-2">
                <Label>Dia da Semana</Label>
                <Select value={String(form.day_of_week)} onValueChange={v => setForm(f => ({ ...f, day_of_week: Number(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAY_NAMES.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.frequency === 'monthly' && (
              <div className="space-y-2">
                <Label>Dia do Mês</Label>
                <Input
                  type="number" min={1} max={28}
                  value={form.day_of_month}
                  onChange={e => setForm(f => ({ ...f, day_of_month: Number(e.target.value) }))}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Horário de Envio</Label>
              <Select value={String(form.hour)} onValueChange={v => setForm(f => ({ ...f, hour: Number(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Grupos de Hosts</Label>
              <Select value={selectedGroup} onValueChange={addGroup}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione um grupo para adicionar..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {hostGroups.filter(g => !form.group_ids.includes(g.id)).map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.group_ids.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {form.group_ids.map(gid => {
                    const g = hostGroups.find(h => h.id === gid);
                    return (
                      <Badge key={gid} variant="secondary" className="cursor-pointer"
                        onClick={() => setForm(f => ({ ...f, group_ids: f.group_ids.filter(id => id !== gid) }))}>
                        {g?.name || gid} ✕
                      </Badge>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-muted-foreground">Vazio = todos os grupos</p>
            </div>

            <div className="space-y-2">
              <Label>Destinatários (emails separados por vírgula) *</Label>
              <Input
                value={form.recipients}
                onChange={e => setForm(f => ({ ...f, recipients: e.target.value }))}
                placeholder="admin@empresa.com, gestor@empresa.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? 'Criando...' : 'Criar Relatório'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
